/**
 * Persists lead details table column order and visibility (Opportunities and Order History).
 * Stored in localStorage so the same order applies for any patient when the user returns.
 */

export const LEAD_DETAILS_COLUMN_STORAGE_KEYS = {
  opportunities: "lead-details-opportunities-columns",
  /** v2: Booking date / Booking time removed (merged into Appointment Date & Time). */
  /** v3: Default column order updated (Services first after fixed Opportunity Name). */
  orderHistory: "lead-details-order-history-columns-v3",
} as const;

export interface ColumnConfigForStorage {
  id: string;
  label: string;
  width: string;
  visible: boolean;
  order: number;
}

type StoredColumnState = Record<string, { order: number; visible: boolean }>;

function getStoredState(key: string): StoredColumnState | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== "object") return null;
    const state: StoredColumnState = {};
    for (const [id, val] of Object.entries(parsed)) {
      if (typeof id === "string" && val !== null && typeof val === "object" && "order" in val && "visible" in val) {
        const v = val as { order: number; visible: boolean };
        if (typeof v.order === "number" && typeof v.visible === "boolean") {
          state[id] = { order: v.order, visible: v.visible };
        }
      }
    }
    return Object.keys(state).length > 0 ? state : null;
  } catch {
    return null;
  }
}

/**
 * Merges saved column order/visibility with default columns.
 * New columns in defaults get appended; unknown saved ids are ignored.
 */
export function getStoredColumnConfig(
  key: string,
  defaultColumns: ColumnConfigForStorage[]
): ColumnConfigForStorage[] {
  const stored = getStoredState(key);
  const merged = defaultColumns.map((d) => {
    const s = stored?.[d.id];
    return s !== undefined
      ? { ...d, order: s.order, visible: s.visible }
      : d;
  });
  merged.sort((a, b) => a.order - b.order);
  return merged.map((c, i) => ({ ...c, order: i + 1 }));
}

export function saveColumnConfig(
  key: string,
  columns: ColumnConfigForStorage[]
): void {
  try {
    const state: StoredColumnState = {};
    columns.forEach((c) => {
      state[c.id] = { order: c.order, visible: c.visible };
    });
    localStorage.setItem(key, JSON.stringify(state));
  } catch {
    // ignore storage errors
  }
}
